import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Pause, Play, Square, Loader2 } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { CAMP_STATUS, fmtDate } from "./shared";
import { ContactsReport } from "./ContactsReport";

const CARD = "rounded-2xl border border-white/10 bg-white/[0.03] p-3";
const pct = (a, b) => (b ? Math.round((a * 1000) / b) / 10 : 0);
const TIP = { background: "#141110", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 };
const longDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return "—"; } };

const Box = ({ label, value, sub, testid }) => (
  <div className={CARD} data-testid={testid}>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="mt-0.5 text-xl font-bold tabular-nums text-white">{value}</p>
    {sub && <p className="text-[11px] text-sky-400">{sub}</p>}
  </div>
);

export const CampaignDetail = ({ id, onBack }) => {
  const [d, setD] = useState(null);
  const [rk, setRk] = useState(0);
  const load = () => api.get(`/admin/email-campaigns/${id}/analytics`).then((r) => setD(r.data)).catch(() => toast.error("Campagne introuvable"));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); setRk((k) => k + 1); } catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); } };
  const setStatus = (status, msg) => act(() => api.patch(`/admin/email-campaigns/${id}`, { status }), msg);
  const resume = () => act(() => api.post(`/admin/email-campaigns/${id}/launch`), "Envoi repris");
  const stop = () => { if (window.confirm("Arrêter définitivement l'envoi de cette campagne ? Les contacts en attente ne recevront rien.")) setStatus("stopped", "Campagne arrêtée"); };

  if (!d) return <Loader2 className="animate-spin text-muted-foreground" />;
  const c = d.campaign, s = d.stats, st = CAMP_STATUS[c.status] || CAMP_STATUS.draft;
  const prog = s.total ? (s.processed * 100) / s.total : 0;
  const hours = d.hours.map((v, h) => ({ h: `${h}h`, v }));
  return (
    <div className="space-y-3" data-testid="campaign-detail">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-white" data-testid="detail-back"><ArrowLeft size={14} /> Campagnes</button>
        <h3 className="text-lg font-bold text-white" data-testid="detail-name">{c.name}</h3>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${st.cls}`} data-testid="detail-status">{st.label}</span>
        <div className="ml-auto flex gap-2">
          {c.status === "active" && <Button size="sm" variant="outline" className="rounded-md" onClick={() => setStatus("paused", "Campagne en pause")} data-testid="detail-pause"><Pause size={14} className="mr-1" /> Pause</Button>}
          {["paused", "draft", "stopped"].includes(c.status) && <Button size="sm" variant="outline" className="rounded-md border-emerald-400/40 text-emerald-300" onClick={resume} data-testid="detail-resume"><Play size={14} className="mr-1" /> {c.status === "draft" ? "Lancer" : "Reprendre"}</Button>}
          {["active", "paused"].includes(c.status) && <Button size="sm" className="rounded-md bg-rose-600 text-white hover:bg-rose-500" onClick={stop} data-testid="detail-stop"><Square size={14} className="mr-1" /> Arrêter</Button>}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">Objet : {c.subject}{c.launched_at ? ` · lancée le ${fmtDate(c.launched_at)}` : ""}</p>

      <div className={`${CARD} p-4`} data-testid="detail-progress">
        <div className="mb-2 flex flex-wrap justify-between gap-2 text-xs">
          <span className="font-semibold text-white">{s.processed.toLocaleString("fr-FR")} / {s.total.toLocaleString("fr-FR")} traités</span>
          <span className="text-muted-foreground">{s.pending.toLocaleString("fr-FR")} en attente{d.eta ? ` · fin estimée le ${longDate(d.eta)}` : ""} · {d.daily_limit.toLocaleString("fr-FR")}/jour</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-sky-500" style={{ width: `${Math.max(prog, prog ? 0.6 : 0)}%` }} /></div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
        <Box label="Délivrés" value={s.sent} sub={`${pct(s.sent, s.processed)} %`} testid="detail-delivered" />
        <Box label="Ouvertures" value={s.opened} sub={`${pct(s.opened, s.sent)} % · ${s.opens_total} au total`} testid="detail-opened" />
        <Box label="Clics" value={s.clicked} sub={`${pct(s.clicked, s.sent)} % · ${s.clicks_total} au total`} testid="detail-clicked" />
        <Box label="Clics / ouvertures" value={`${pct(s.clicked, s.opened)} %`} testid="detail-ctor" />
        <Box label="Inscriptions" value={s.registered} sub={`${pct(s.registered, s.sent)} %`} testid="detail-registered" />
        <Box label="Achats de bougies" value={s.purchased} sub={`${pct(s.purchased, s.sent)} %`} testid="detail-purchased" />
        <Box label="Désinscriptions" value={s.unsubscribed} sub={`${pct(s.unsubscribed, s.sent)} %`} testid="detail-unsub" />
        <Box label="Adresses en erreur" value={s.errors} sub={`${s.invalid} invalide(s) · ${s.ignored} ignorée(s)`} testid="detail-errors" />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className={`${CARD} p-4`} data-testid="detail-chart-days">
          <p className="mb-2 text-sm font-semibold text-white">Jour par jour</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.days}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9a9aa0" }} />
              <YAxis tick={{ fontSize: 10, fill: "#9a9aa0" }} allowDecimals={false} />
              <Tooltip contentStyle={TIP} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="sent" name="Envoyés" stroke="#3b82f6" strokeWidth={2} />
              <Line type="monotone" dataKey="opened" name="Ouvertures" stroke="#22c55e" strokeWidth={2} />
              <Line type="monotone" dataKey="clicked" name="Clics" stroke="#f59e0b" strokeWidth={2} />
              <Line type="monotone" dataKey="registered" name="Inscriptions" stroke="#ec4899" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className={`${CARD} p-4`} data-testid="detail-chart-hours">
          <p className="mb-2 text-sm font-semibold text-white">Heures d'ouverture (heure de Paris){d.best_hour != null && <span className="ml-1 text-xs font-normal text-muted-foreground">· meilleure heure : <span className="text-sky-400">{d.best_hour}h</span></span>}</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hours}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="h" tick={{ fontSize: 10, fill: "#9a9aa0" }} interval={1} />
              <YAxis tick={{ fontSize: 10, fill: "#9a9aa0" }} allowDecimals={false} />
              <Tooltip contentStyle={TIP} />
              <Bar dataKey="v" name="Ouvertures" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <ContactsReport cid={id} refreshKey={rk} />
    </div>
  );
};
